import { useState } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supportService } from '../../services/supportService';
import { useCopy } from '../../i18n';
import { showToast } from '../common/Toast';

interface SatisfactionRatingProps {
  ticketId: string;
  onRated: () => void;
}

const SatisfactionRating = ({ ticketId, onRated }: SatisfactionRatingProps) => {
  const t = useCopy().account.support.rating;
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) return;

    setSubmitting(true);
    try {
      await supportService.rateTicket(ticketId, rating, comment.trim() || undefined);
      showToast(t.thanks, 'success');
      onRated();
    } catch (err) {
      console.error('Error rating ticket:', err);
      showToast(t.error, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
      <p className="text-sm font-medium text-[var(--color-text)] text-center mb-3">
        {t.title}
      </p>

      {/* Stars */}
      <div className="flex justify-center gap-1 mb-3">
        {[1, 2, 3, 4, 5].map(value => (
          <button
            key={value}
            className="support-star p-1"
            onMouseEnter={() => setHovered(value)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => setRating(value)}
          >
            <Star
              size={24}
              fill={(hovered || rating) >= value ? '#f59e0b' : 'none'}
              color={(hovered || rating) >= value ? '#f59e0b' : 'var(--color-text-muted)'}
            />
          </button>
        ))}
      </div>

      {rating > 0 && (
        <>
          <textarea
            className="w-full p-2.5 text-sm border border-[var(--color-border)] rounded-[var(--radius-md,6px)] bg-[var(--color-bg)] text-[var(--color-text)] font-[inherit] resize-none focus:outline-none focus:border-[var(--color-primary)]"
            rows={2}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={t.commentPlaceholder}
            maxLength={500}
          />
          <Button
            className="w-full mt-2"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            {t.submit}
          </Button>
        </>
      )}
    </div>
  );
};

export default SatisfactionRating;
